/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/section-color-palette.ts
 * FILE_ID: QB-AGGRID-SECTION-PALETTE-001
 * PURPOSE: Pure I→O picker mapping a section identifier to a stable
 *          colour band from a fixed palette. Used by the SAM-style
 *          full-width section banner: each section gets one of a
 *          handful of pastel tints so estimators can scan a multi-
 *          sheet quote and tell sections apart at a glance.
 * WHY: SAM renders inline section banner rows in stable per-section
 *      colours (the screenshot shows amber Roofs, sky External Walls,
 *      emerald Soffits, etc.). Quotech's tabbed view had a single
 *      monochrome chrome — visually flat. This palette wires SAM's
 *      colour-band metaphor into the existing toolbar + tab strip
 *      without changing the data model.
 * FLOW:
 *   ┌────────────────┐   ┌─────────────────────┐   ┌──────────────────────┐
 *   │ section.id /   │ → │ hashSeed → palette  │ → │ classes for          │
 *   │ section.title  │   │   slot (mod N)      │   │ tab / banner / dot   │
 *   └────────────────┘   └─────────────────────┘   └──────────────────────┘
 * DEPENDENCIES: none.
 * RELATED:
 *   - QB-AGGRID-TOOLBAR-001 (ScopeSectionToolbar) — uses banner/text
 *   - QB-AGGRID-TABSTRIP-001 (ScopeAgGridTabStrip) — uses dot/active
 */

export interface SectionColorBand {
  /** Human-readable label, used in tests/debug + as a stable identifier. */
  name:
    | 'amber'
    | 'sky'
    | 'emerald'
    | 'violet'
    | 'rose'
    | 'slate'
    | 'orange'
    | 'cyan'
  /** Tailwind class string for the full-width banner background tint. */
  bannerBg: string
  /** Tailwind class for the banner's left-edge border accent. */
  bannerBorder: string
  /** Tailwind class for the section title text colour in the banner. */
  titleText: string
  /** Tailwind class for the tab-strip dot indicator (active section). */
  dot: string
  /** Tailwind class for the tab-strip bottom underline (active tab).
   *  Distinct from bannerBorder so JIT sees both as static strings. */
  tabUnderline: string
}

/**
 * Stable palette — 8 slots. Picked to be (a) visually distinct, (b)
 * legible against both light and dark theme backgrounds, and (c)
 * accessible (no near-duplicate hues). Adding a new colour is fine
 * — order is the contract, slots map to seeds via modulo.
 */
export const SECTION_COLOR_PALETTE: ReadonlyArray<SectionColorBand> = [
  {
    name: 'amber',
    bannerBg: 'bg-amber-50/70 dark:bg-amber-950/30',
    bannerBorder: 'border-l-amber-500',
    titleText: 'text-amber-800 dark:text-amber-300',
    dot: 'bg-amber-500',
    tabUnderline: 'border-b-amber-500',
  },
  {
    name: 'sky',
    bannerBg: 'bg-sky-50/70 dark:bg-sky-950/30',
    bannerBorder: 'border-l-sky-500',
    titleText: 'text-sky-800 dark:text-sky-300',
    dot: 'bg-sky-500',
    tabUnderline: 'border-b-sky-500',
  },
  {
    name: 'emerald',
    bannerBg: 'bg-emerald-50/70 dark:bg-emerald-950/30',
    bannerBorder: 'border-l-emerald-500',
    titleText: 'text-emerald-800 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    tabUnderline: 'border-b-emerald-500',
  },
  {
    name: 'violet',
    bannerBg: 'bg-violet-50/70 dark:bg-violet-950/30',
    bannerBorder: 'border-l-violet-500',
    titleText: 'text-violet-800 dark:text-violet-300',
    dot: 'bg-violet-500',
    tabUnderline: 'border-b-violet-500',
  },
  {
    name: 'rose',
    bannerBg: 'bg-rose-50/70 dark:bg-rose-950/30',
    bannerBorder: 'border-l-rose-500',
    titleText: 'text-rose-800 dark:text-rose-300',
    dot: 'bg-rose-500',
    tabUnderline: 'border-b-rose-500',
  },
  {
    name: 'cyan',
    bannerBg: 'bg-cyan-50/70 dark:bg-cyan-950/30',
    bannerBorder: 'border-l-cyan-500',
    titleText: 'text-cyan-800 dark:text-cyan-300',
    dot: 'bg-cyan-500',
    tabUnderline: 'border-b-cyan-500',
  },
  {
    name: 'orange',
    bannerBg: 'bg-orange-50/70 dark:bg-orange-950/30',
    bannerBorder: 'border-l-orange-500',
    titleText: 'text-orange-800 dark:text-orange-300',
    dot: 'bg-orange-500',
    tabUnderline: 'border-b-orange-500',
  },
  {
    name: 'slate',
    bannerBg: 'bg-slate-100/70 dark:bg-slate-800/40',
    bannerBorder: 'border-l-slate-500',
    titleText: 'text-slate-800 dark:text-slate-300',
    dot: 'bg-slate-500',
    tabUnderline: 'border-b-slate-500',
  },
]

/**
 * Deterministic 32-bit hash of an arbitrary string. Used to pick a
 * stable palette slot for a section id — identical id ⇒ identical
 * colour across reloads.
 *
 * NOT cryptographic. Borrowed from djb2.
 */
export function hashSeed(seed: string): number {
  let h = 5381
  for (let i = 0; i < seed.length; i++) {
    // bitwise ops + ASCII char codes — keeps result in 32-bit range
    h = (h * 33) ^ seed.charCodeAt(i)
  }
  // Force unsigned 32-bit for stable modulo behaviour
  return h >>> 0
}

/**
 * Pick the colour band for a given section seed. Empty seed falls
 * back to slot 0 (amber) so the UI never crashes — section ids
 * should always be non-empty in practice.
 */
export function getSectionColor(seed: string | null | undefined): SectionColorBand {
  const safe = (seed ?? '').trim()
  const slot = safe.length === 0 ? 0 : hashSeed(safe) % SECTION_COLOR_PALETTE.length
  return SECTION_COLOR_PALETTE[slot]
}
